import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { ClockIcon } from "@heroicons/react/24/solid";
import { IDashboardStats, UserActivityStats } from "../../models/DashboardStats";
import DashboardStatTableTabs from "../shared/Tabs/DashboardStatTableTabs";
import DashboardTile from "./DashboardTile";
import { useNextCronCountdown } from "../../data/useNextCronCountdown";

const tabs = [
  { name: 'Today', value: 'today' },
  { name: 'Week', value: 'week' },
  { name: 'Month', value: 'month' },
];

interface IActivityTableProps {
  todayStats: IDashboardStats;
  weekStats: IDashboardStats;
  monthStats: IDashboardStats;
  title: string;
  keyCol: string;
}

export default function PartsUserActivityTable({
  todayStats,
  weekStats,
  monthStats,
  title,
  keyCol,
}: IActivityTableProps) {
  const [timeframe, setTimeframe] = useState("today");
  const [userActivity, setUserActivity] = useState<{ [key: string]: UserActivityStats } | undefined>(undefined);
  const [userActivityTotal, setUserActivityTotal] = useState<UserActivityStats | undefined>(undefined);
  const [combinedKeys, setCombinedKeys] = useState<string[]>([]);
  const countdown = useNextCronCountdown();
  const [showTooltip, setShowTooltip] = useState(false);

  const days = useMemo(() => {
    if (timeframe === "today") return 1;
    if (timeframe === "week") return DateTime.now().weekday;
    return DateTime.now().day;
  }, [timeframe]);
  const showDailyAvg = timeframe !== "today" && days >= 1;
  const displayKeys = useMemo(() => (
    userActivityTotal != null ? ["Total", ...combinedKeys] : combinedKeys
  ), [combinedKeys, userActivityTotal]);

  useEffect(() => {
    const selectedRaw =
      timeframe === "week"
        ? weekStats
        : timeframe === "month"
          ? monthStats
          : todayStats;
    const activity = selectedRaw.userActivity ?? {};
    setUserActivity(activity);
    setUserActivityTotal(selectedRaw.userActivityTotal);
    setCombinedKeys(
      Object.keys(activity)
        .filter((k) => k !== "Total")
        .sort((a, b) => a.localeCompare(b))
    );
  }, [todayStats, weekStats, monthStats, timeframe]);

  return (
    <DashboardTile span={2}>
      <div className="p-3">
        <div className="mb-3 w-full flex justify-between">
          <div className="prose">
            <h3>
              <div
                className="relative inline-flex items-center"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <ClockIcon className="inline-block w-6 h-6 mr-2 text-gray-500 hover:text-primary transition-all duration-200 hover:scale-110 cursor-pointer" />
                {title}
                {showTooltip && (
                  <div className="absolute left-0 top-full mt-2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs text-white shadow-lg z-10">
                    Recalculating in {countdown}
                  </div>
                )}
              </div>
            </h3>
          </div>

          <DashboardStatTableTabs tabs={tabs} value={timeframe} onChange={setTimeframe} ariaLabel="Select a tab" />
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full border-collapse text-center text-xs">
            <thead>
              <tr>
                <th className="text-left pb-2">{keyCol}</th>
                <th>Total</th>
                <th className="bg-primary-50">Vehicles Booked</th>
                <th>Vehicles Collected</th>
                <th className="bg-primary-50">Jobs Created</th>
                <th>Parts 
                  Created</th>
                <th className="bg-primary-50">Removed</th>
                <th>Imaged</th>
                <th className="bg-primary-50">Located</th>
                <th>Priced</th>
                <th className="bg-primary-50">Listed</th>
                <th>Picked</th>
                <th className="bg-primary-50">Shipped</th>
                <th>Scrapped</th>
                <th className="bg-primary-50">Deleted</th>
                <th>Damage Reports</th>
                {showDailyAvg ? <th className="bg-primary-50">Daily avg</th> : null}
                {showDailyAvg ? <th>Avg/hr</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y">
              {displayKeys.length === 0 ? (
                <tr>
                  <td colSpan={showDailyAvg ? 18 : 16} className="py-6 text-center text-gray-500">
                    No activity data for this period.
                  </td>
                </tr>
              ) : (
                displayKeys.map((key) => {
                  const isTotalRow = key === "Total";
                  const row = isTotalRow ? userActivityTotal : userActivity?.[key];
                  const booked = row?.booked ?? 0;
                  const collected = row?.collected ?? 0;
                  const dismantling = row?.dismantlingListsCreated ?? 0;

                  const partsTotal =
                    (row?.createdCount ?? 0) +
                    (row?.removedCount ?? 0) +
                    (row?.imagedCount ?? 0) +
                    (row?.locatedCount ?? 0) +
                    (row?.pricedCount ?? 0) +
                    (row?.listedCount ?? 0) +
                    (row?.pickedCount ?? 0) +
                    (row?.shippedCount ?? 0) +
                    (row?.scrappedCount ?? 0) +
                    (row?.deletedCount ?? 0) +
                    (row?.damagedCount ?? 0);

                  const rowTotal = booked + collected + dismantling + partsTotal;
                  if (!isTotalRow && rowTotal === 0) return null;

                  const dailyAvg = showDailyAvg && days >= 1 ? partsTotal / days : null;
                  const avgPerHour = dailyAvg != null ? dailyAvg / 8 : null;
                  const cellClass = (val: number | string | undefined | null, stripe = false) =>
                    `${val === 0 || val === "—" || val === undefined || val === null ? "text-gray-400" : "text-primary-600"} font-bold text-md ${stripe ? "bg-primary-50" : ""}`;

                  return (
                    <tr
                      key={key}
                      className={`font-semibold text-center ${isTotalRow ? "border-t-2 border-gray-300 bg-gray-50" : ""}`}
                    >
                      <td className="text-left pt-2" title={key}>
                        {key}
                      </td>
                      <td className={cellClass(rowTotal)}>{rowTotal}</td>
                      <td className={cellClass(booked, true)}>{booked}</td>
                      <td className={cellClass(collected)}>{collected}</td>
                      <td className={cellClass(dismantling, true)}>{dismantling}</td>
                      <td className={cellClass(row?.createdCount)}>{row?.createdCount ?? "—"}</td>
                      <td className={cellClass(row?.removedCount, true)}>{row?.removedCount ?? "—"}</td>
                      <td className={cellClass(row?.imagedCount)}>{row?.imagedCount ?? "—"}</td>
                      <td className={cellClass(row?.locatedCount, true)}>{row?.locatedCount ?? "—"}</td>
                      <td className={cellClass(row?.pricedCount)}>{row?.pricedCount ?? "—"}</td>
                      <td className={cellClass(row?.listedCount, true)}>{row?.listedCount ?? "—"}</td>
                      <td className={cellClass(row?.pickedCount)}>{row?.pickedCount ?? "—"}</td>
                      <td className={cellClass(row?.shippedCount, true)}>{row?.shippedCount ?? "—"}</td>
                      <td className={cellClass(row?.scrappedCount)}>{row?.scrappedCount ?? "—"}</td>
                      <td className={cellClass(row?.deletedCount, true)}>{row?.deletedCount ?? "—"}</td>
                      <td className={cellClass(row?.damagedCount)}>{row?.damagedCount ?? "—"}</td>
                      {showDailyAvg ? (
                        <td className={cellClass(dailyAvg != null ? dailyAvg : "—", true)}>
                          {dailyAvg != null ? dailyAvg.toFixed(1) : "—"}
                        </td>
                      ) : null}
                      {showDailyAvg ? (
                        <td className={cellClass(avgPerHour != null ? avgPerHour : "—")}>
                          {avgPerHour != null ? avgPerHour.toFixed(1) : "—"}
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}